import { useState } from "react";

import { useForm } from "react-hook-form";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from "@mui/material";
import { yupResolver } from '@hookform/resolvers/yup';

import { ErrorAlert, SuccessAlert } from "@shared/ui/alerts";
import { sendManualInvoice } from "../api/invoiceApi";
import { buildChargedInvoiceSchema } from "../schema/chargedInvoiceSchema";
import { ChargedAutomatic } from "./ChargedAutomatic";
import { isAllowedInvoiceFileType, isWithinInvoiceFileSize } from "../lib/invoiceFileRules";
import { buildManualInvoiceFormData } from "../lib/invoicePayloads";

import type { ChangeEvent } from "react";
import type { TFunction } from "i18next";
import type { ManualInvoiceFormValues } from "../lib/invoicePayloads";

export interface ChargedInvoiceDialogProps {
    handleClose: () => void;
    open: boolean;
    notLoading: () => void;
    loading: () => void;
    title: string;
    subTypes: string;
    t: TFunction;
    onSuccess?: () => void;
}

interface ValidateFileState {
    required: boolean;
    type: boolean;
    size: boolean;
}

const validationFileInitial: ValidateFileState = {
    required: false,
    type: false,
    size: false
}

export const ChargedInvoiceDialog = ({handleClose, open, notLoading, loading, title, subTypes, t, onSuccess}: ChargedInvoiceDialogProps) => {
    const schema = buildChargedInvoiceSchema(t);

    // El schema de yup (`chargedInvoiceSchema`) solo valida ccEmail/invoiceDate/invoiceNumber/total;
    // el formulario real también incluye subtotal/itbmsTax/comments (ver `ChargedAutomatic`), así que
    // se tipa el form completo con `ManualInvoiceFormValues` (mismo tipo que consume
    // `buildManualInvoiceFormData`) y se castea el resolver, igual que en PostulateCandidateDialog.
    const {control, handleSubmit, reset, watch, formState: {errors}} = useForm<ManualInvoiceFormValues>({
        resolver: yupResolver(schema) as never,
    });


    const [file, setFile] = useState<File | undefined>()
    const [validateFile, setValidateFile] = useState<ValidateFileState>(validationFileInitial)

    const resetField = () => {
        reset({
            ccEmail: "",
            invoiceDate: new Date(),
            invoiceNumber: 0,
            total:"",
            comments: "",
            itbmsTax:0,
            subtotal: 0,
        })
        setFile(undefined)
    }

    const validateSizeFile = (files: File) => {
        const tooLarge = !isWithinInvoiceFileSize(files)
        setValidateFile({ required: false, type: false, size: tooLarge })
        return tooLarge
    }

    const validateTypeFile = (files: File) => {
        const allowed = isAllowedInvoiceFileType(files)
        setValidateFile({ required: false, type: !allowed, size: false })
        return allowed
    }

    const requiredFile = () => {
        setValidateFile({
            type: false,
            size: false,
            required: true,
        })
    }

    const handleChangeFile = (e: ChangeEvent<HTMLInputElement>) => {
        // Comportamiento preexistente (ver mismo patrón en usePermission.ts): `files` puede ser
        // `null` en el tipo del DOM, pero el runtime siempre lo llena en este flujo de carga.
        if (e.target.files!.length) {
            // `length` distinto de cero garantiza que el primer elemento existe.
            const selectedFile = e.target.files![0]!
            let isFileType = validateTypeFile(selectedFile)
            if (isFileType) {
                let isFileSize = validateSizeFile(selectedFile)
                if (!isFileSize) {
                    setFile(selectedFile)
                }
            }
            return null
        }
        requiredFile()
    };

    const onClose = () => {
        setFile(undefined)
        setValidateFile(validationFileInitial)
        resetField()
        handleClose()
    }

    const onRegister = (value: ManualInvoiceFormValues) => {
        if (file === undefined) {
            requiredFile()
            return null;
        }

        if (!validateTypeFile(file) && validateSizeFile(file)) {
            return null;
        }

        loading()
        onClose()
        const formData = buildManualInvoiceFormData(value, file, subTypes)

        sendManualInvoice(formData).then(() => {
            notLoading()
            if (onSuccess) onSuccess();
            SuccessAlert(t("uploadInvoice"), t("doneSuccessfully", {a:"a"})).then()
        }).catch((error) => {
            const data = error.response?.data;
            notLoading()
            ErrorAlert(t("errorUploadInvoice"), data?.message ?? t("internalError")).then()
        })
    }

    return (
        <>
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <form onSubmit={handleSubmit(onRegister)}>
                    <DialogTitle>{title}</DialogTitle>
                    <DialogContent>
                        <Box sx={{ p: 1 }}>
                            <Grid container spacing={3}>
                                <ChargedAutomatic
                                    control={control}
                                    errors={errors}
                                    file={file}
                                    handleChangeFile={handleChangeFile}
                                    validateFile={validateFile}
                                    t={t}
                                />
                            </Grid>
                        </Box>

                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onClose}>{t("cancel")}</Button>
                        <Button type="submit">{t("charge")}</Button>
                    </DialogActions>
                </form>
            </Dialog>
        </>
    );
}
